/**
 * NotificationsTabContent - Push Notifications management UI
 * Extracted from AdminDashboard.jsx for better maintainability
 */
import React from 'react';
import { format } from 'date-fns';
import { 
  Bell, 
  Plus, 
  Users, 
  Eye, 
  MousePointer, 
  BarChart3, 
  Trash2, 
  X, 
  Send, 
  Upload, 
  Target, 
  Clock, 
  Mail 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const NotificationsTabContent = ({
  notifications,
  notificationAnalytics,
  showNotificationModal,
  setShowNotificationModal,
  newNotification,
  setNewNotification,
  imagePreview,
  setImagePreview,
  handleImageUpload,
  insertMergeTag,
  targetingUsers,
  addActionButton,
  updateActionButton,
  removeActionButton,
  handleCreateNotification,
  handleDeleteNotification,
  openNotificationDetails,
  selectedNotification,
  closeNotificationDetails,
  notificationDetailTab,
  setNotificationDetailTab,
  notificationDetails,
  loadingNotificationDetails,
  resetNotificationForm
}) => {
  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#FF7901]" />
          Push Notifications
        </h2>
        <Button 
          onClick={() => setShowNotificationModal(true)}
          className="bg-gradient-to-r from-[#df1283] to-[#FF7901] hover:opacity-90"
        >
          <Plus className="w-4 h-4 mr-1" /> Create Notification
        </Button>
      </div>

      {/* Analytics Summary Cards */}
      {notificationAnalytics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-white">{notificationAnalytics.summary?.total_sent || 0}</div>
            <div className="text-xs text-[#FDF2F8]/50">Total Sent</div>
          </div>
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-[#0FFFE2]">{notificationAnalytics.summary?.view_rate || 0}%</div>
            <div className="text-xs text-[#FDF2F8]/50">View Rate</div>
            <div className="text-xs text-[#0FFFE2]/70">{notificationAnalytics.summary?.total_views || 0} views</div>
          </div>
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-[#FF7901]">{notificationAnalytics.summary?.click_rate || 0}%</div>
            <div className="text-xs text-[#FDF2F8]/50">Click Rate</div>
            <div className="text-xs text-[#FF7901]/70">{notificationAnalytics.summary?.total_clicks || 0} clicks</div>
          </div>
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-[#df1283]">{notificationAnalytics.counts?.active || 0}</div>
            <div className="text-xs text-[#FDF2F8]/50">Active</div>
            <div className="text-xs text-[#df1283]/70">{notificationAnalytics.counts?.scheduled || 0} scheduled</div>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="space-y-4">
        {notifications.length === 0 ? (
          <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-8 text-center">
            <Bell className="w-12 h-12 text-[#FDF2F8]/30 mx-auto mb-3" />
            <p className="text-[#FDF2F8]/50">No notifications yet</p>
            <p className="text-sm text-[#FDF2F8]/30 mt-1">Create your first notification to engage users</p>
          </div>
        ) : (
          notifications.map((notif) => (
            <NotificationCard 
              key={notif.notification_id}
              notif={notif}
              onView={openNotificationDetails}
              onDelete={handleDeleteNotification}
            />
          ))
        )}
      </div>

      {/* Notification Detail Modal */}
      {selectedNotification && (
        <NotificationDetailModal
          notification={selectedNotification}
          details={notificationDetails}
          loading={loadingNotificationDetails}
          activeTab={notificationDetailTab}
          setActiveTab={setNotificationDetailTab}
          onClose={closeNotificationDetails}
        />
      )}

      {/* Create Notification Modal */}
      {showNotificationModal && (
        <CreateNotificationModal
          notification={newNotification}
          setNotification={setNewNotification}
          imagePreview={imagePreview}
          setImagePreview={setImagePreview}
          handleImageUpload={handleImageUpload}
          insertMergeTag={insertMergeTag}
          targetingUsers={targetingUsers}
          addActionButton={addActionButton}
          updateActionButton={updateActionButton}
          removeActionButton={removeActionButton}
          handleCreate={handleCreateNotification}
          onClose={() => { setShowNotificationModal(false); resetNotificationForm(); }}
        />
      )}
    </div>
  );
};

// Notification Card Component
const NotificationCard = ({ notif, onView, onDelete }) => (
  <div 
    className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66]/50 rounded-xl p-5 hover:border-[#df1283]/50 transition-colors cursor-pointer"
    onClick={() => onView(notif)}
  >
    <div className="flex items-start gap-4">
      {notif.image_url && (
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-black/30">
          <img src={notif.image_url} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            notif.type === 'announcement' ? 'bg-[#FF7901]/20 text-[#FF7901]' :
            notif.type === 'alert' ? 'bg-red-500/20 text-red-400' :
            notif.type === 'promotion' ? 'bg-green-500/20 text-green-400' :
            'bg-[#df1283]/20 text-[#df1283]'
          }`}>
            {notif.type}
          </span>
          {notif.status === 'scheduled' && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-500/20 text-yellow-400">Scheduled</span>
          )}
          {notif.show_popup && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-[#4A0A66]/50 text-[#FDF2F8]/60">Shows as popup</span>
          )}
          {notif.target_type !== 'all' && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-[#0FFFE2]/20 text-[#0FFFE2]">Targeted</span>
          )}
        </div>
        <h3 className="text-white font-semibold mb-1">{notif.title}</h3>
        <p className="text-sm text-[#FDF2F8]/70 line-clamp-2">{notif.message}</p>
        <div className="flex items-center gap-4 mt-3 text-xs flex-wrap">
          <span className="text-[#FDF2F8]/50">
            {format(new Date(notif.created_at), 'MMM d, yyyy h:mm a')}
          </span>
          <span className="flex items-center gap-1 text-white">
            <Users className="w-3 h-3" />
            {notif.total_recipients || 0} recipients
          </span>
          {(notif.view_count > 0 || notif.analytics?.total_views > 0) && (
            <span className="flex items-center gap-1 text-[#0FFFE2]">
              <Eye className="w-3 h-3" />
              {notif.view_count || notif.analytics?.total_views || 0} views
            </span>
          )}
          {(notif.click_count > 0 || notif.analytics?.total_clicks > 0) && (
            <span className="flex items-center gap-1 text-[#FF7901]">
              <MousePointer className="w-3 h-3" />
              {notif.click_count || notif.analytics?.total_clicks || 0} clicks
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onView(notif)} className="text-[#0FFFE2]/60 hover:text-[#0FFFE2] p-2" title="View Analytics">
          <BarChart3 className="w-4 h-4" />
        </button>
        <button onClick={() => onDelete(notif.notification_id)} className="text-red-400/60 hover:text-red-400 p-2" title="Delete">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
);

// Notification Detail Modal
const NotificationDetailModal = ({ notification, details, loading, activeTab, setActiveTab, onClose }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
    <div 
      className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#4A0A66]/50">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#FF7901]" />
            {notification.title}
          </h3>
          <p className="text-sm text-[#FDF2F8]/50 mt-1">
            Sent {format(new Date(notification.created_at), 'MMM d, yyyy h:mm a')}
          </p>
        </div>
        <button onClick={onClose} className="text-[#FDF2F8]/50 hover:text-white p-2">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#4A0A66]/50 px-6">
        {[
          { id: 'stats', label: 'Stats', icon: BarChart3 },
          { id: 'recipients', label: `Recipients (${details?.total_recipients || 0})`, icon: Users },
          { id: 'views', label: `Views (${details?.viewed_users?.length || 0})`, icon: Eye },
          { id: 'clicks', label: `Clicks (${details?.clicked_users?.length || 0})`, icon: MousePointer },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id ? 'text-[#0FFFE2] border-[#0FFFE2]' : 'text-[#FDF2F8]/50 border-transparent hover:text-white'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-[#df1283] border-t-transparent rounded-full" />
          </div>
        ) : activeTab === 'stats' ? (
          <StatsTabContent notification={notification} details={details} />
        ) : activeTab === 'recipients' ? (
          <div className="text-center text-[#FDF2F8]/40 py-8">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>This notification was sent to {details?.total_recipients || 0} users.</p>
            <p className="text-xs mt-1">See Views and Clicks tabs for user details</p>
          </div>
        ) : activeTab === 'views' ? (
          <UserListTab users={details?.viewed_users} type="view" />
        ) : activeTab === 'clicks' ? (
          <UserListTab users={details?.clicked_users} type="click" />
        ) : null}
      </div>
    </div>
  </div>
);

// Stats Tab Content
const StatsTabContent = ({ notification, details }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <StatBox value={details?.total_recipients || 0} label="Recipients" color="white" />
      <StatBox 
        value={`${details?.total_recipients > 0 ? Math.round((details?.viewed_users?.length || 0) / details.total_recipients * 100) : 0}%`} 
        label="View Rate" 
        subtext={`${details?.viewed_users?.length || 0} views`}
        color="#0FFFE2" 
      />
      <StatBox 
        value={`${details?.total_recipients > 0 ? Math.round((details?.clicked_users?.length || 0) / details.total_recipients * 100) : 0}%`} 
        label="Click Rate" 
        subtext={`${details?.clicked_users?.length || 0} clicks`}
        color="#FF7901" 
      />
      <StatBox value={details?.dismissed_users?.length || 0} label="Dismissed" color="#df1283" />
    </div>

    {/* Engagement Funnel */}
    <div className="bg-[#4A0A66]/20 rounded-xl p-6">
      <h4 className="text-white font-semibold mb-4">Engagement Funnel</h4>
      <div className="space-y-4">
        <FunnelBar label="Sent" count={details?.total_recipients || 0} total={details?.total_recipients || 1} color="white" />
        <FunnelBar label="Viewed" count={details?.viewed_users?.length || 0} total={details?.total_recipients || 1} color="#0FFFE2" />
        <FunnelBar label="Clicked" count={details?.clicked_users?.length || 0} total={details?.total_recipients || 1} color="#FF7901" />
      </div>
    </div>

    {/* Preview */}
    <div className="bg-[#4A0A66]/20 rounded-xl p-6">
      <h4 className="text-white font-semibold mb-4">Notification Preview</h4>
      <div className="bg-[#1a0527] rounded-lg p-4 border border-[#4A0A66]/50">
        {notification.image_url && (
          <img src={notification.image_url} alt="" className="w-full max-h-48 object-contain rounded-lg mb-4" />
        )}
        <h5 className="text-lg font-semibold text-white mb-2">{notification.title}</h5>
        <p className="text-sm text-[#FDF2F8]/70">{notification.message}</p>
      </div>
    </div>
  </div>
);

const StatBox = ({ value, label, subtext, color }) => (
  <div className="bg-[#4A0A66]/20 rounded-xl p-4 text-center">
    <div className="text-3xl font-bold" style={{ color }}>{value}</div>
    <div className="text-sm text-[#FDF2F8]/50 mt-1">{label}</div>
    {subtext && <div className="text-xs mt-1" style={{ color: `${color}99` }}>{subtext}</div>}
  </div>
);

const FunnelBar = ({ label, count, total, color }) => (
  <div>
    <div className="flex justify-between text-sm mb-1">
      <span className="text-[#FDF2F8]/70">{label}</span>
      <span style={{ color }}>{count}</span>
    </div>
    <div className="h-8 bg-[#4A0A66]/30 rounded-full overflow-hidden">
      <div 
        className="h-full rounded-full transition-all duration-500" 
        style={{ 
          width: `${(count / total) * 100}%`,
          background: color === 'white' ? 'white' : `linear-gradient(to right, ${color}, ${color}99)`
        }}
      />
    </div>
  </div>
);

const UserListTab = ({ users, type }) => {
  if (!users || users.length === 0) {
    const Icon = type === 'view' ? Eye : MousePointer;
    return (
      <div className="text-center py-8 text-[#FDF2F8]/40">
        <Icon className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p>No {type === 'view' ? 'views' : 'clicks'} tracked yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {users.map((user, idx) => (
        <div key={user.user_id || idx} className="flex items-center justify-between bg-[#4A0A66]/20 rounded-lg p-3">
          <div className="flex items-center gap-3">
            <div 
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                type === 'view' ? 'bg-gradient-to-br from-[#0FFFE2] to-[#0FFFE2]/50 text-black' : 'bg-gradient-to-br from-[#FF7901] to-[#FF7901]/50 text-white'
              }`}
            >
              {(user.name || user.email || 'U')[0].toUpperCase()}
            </div>
            <div>
              <p className="text-white font-medium">{user.name || 'Unknown'}</p>
              <p className="text-xs text-[#FDF2F8]/50">{user.email}</p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-xs ${type === 'view' ? 'text-[#0FFFE2]' : 'text-[#FF7901]'}`}>
              {type === 'view' ? 'Viewed' : 'Clicked'}
            </p>
            <p className="text-xs text-[#FDF2F8]/50">
              {user.timestamp ? format(new Date(user.timestamp), 'MMM d, h:mm a') : 'Unknown'}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Create Notification Modal
const CreateNotificationModal = ({
  notification,
  setNotification,
  imagePreview,
  setImagePreview,
  handleImageUpload,
  insertMergeTag,
  targetingUsers,
  addActionButton,
  updateActionButton,
  removeActionButton,
  handleCreate,
  onClose
}) => (
  <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
    <div className="bg-gradient-to-br from-[#2D063E] to-[#1a0527] border border-[#4A0A66] rounded-2xl p-6 w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Send className="w-5 h-5 text-[#FF7901]" />
          Create Notification
        </h3>
        <button onClick={onClose} className="text-[#FDF2F8]/50 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-5">
        {/* Image Upload */}
        <div>
          <label className="block text-sm text-[#FDF2F8]/70 mb-1">Image/Flyer (optional)</label>
          {imagePreview ? (
            <div className="relative">
              <img src={imagePreview} alt="Preview" className="w-full max-h-48 object-contain rounded-lg bg-black/30" />
              <button
                onClick={() => { setImagePreview(null); setNotification({...notification, image_data: null}); }}
                className="absolute top-2 right-2 p-2 rounded-full bg-red-500/80 hover:bg-red-500 text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-[#4A0A66] rounded-lg cursor-pointer hover:border-[#FF7901]/50 transition-colors bg-[#4A0A66]/10">
              <Upload className="w-6 h-6 text-[#FDF2F8]/40 mb-1" />
              <span className="text-sm text-[#FDF2F8]/50">Click to upload</span>
              <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
            </label>
          )}
        </div>

        {/* Title */}
        <div>
          <label className="text-sm text-[#FDF2F8]/70">Title</label>
          <Input
            value={notification.title}
            onChange={(e) => setNotification({...notification, title: e.target.value})}
            placeholder="e.g., Hey {{first_name}}, check this out!"
            className="bg-[#4A0A66]/30 border-[#4A0A66] text-white mt-1"
          />
        </div>

        {/* Message */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-[#FDF2F8]/70">Message</label>
            <select
              onChange={(e) => { if (e.target.value) insertMergeTag(e.target.value); e.target.value = ''; }}
              className="text-xs bg-[#4A0A66]/50 border border-[#4A0A66] rounded px-2 py-1 text-[#0FFFE2] cursor-pointer"
            >
              <option value="">+ Insert Tag</option>
              <option value="{{first_name}}">First Name</option>
              <option value="{{name}}">Full Name</option>
              <option value="{{streak}}">Streak Count</option>
              <option value="{{email}}">Email</option>
            </select>
          </div>
          <textarea
            value={notification.message}
            onChange={(e) => setNotification({...notification, message: e.target.value})}
            placeholder="Write your notification message..."
            rows={3}
            className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded-md px-3 py-2 text-white placeholder:text-[#FDF2F8]/30 focus:outline-none focus:border-[#FF7901]"
          />
        </div>

        {/* Type and Popup */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-[#FDF2F8]/70 mb-1">Type</label>
            <select
              value={notification.type}
              onChange={(e) => setNotification({...notification, type: e.target.value})}
              className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded-md px-3 py-2 text-white"
            >
              <option value="announcement">Announcement</option>
              <option value="info">Info</option>
              <option value="alert">Alert</option>
              <option value="promotion">Promotion</option>
            </select>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notification.show_popup}
                onChange={(e) => setNotification({...notification, show_popup: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-[#FDF2F8]/70">Show as popup</span>
            </label>
          </div>
        </div>

        {/* Email Option */}
        <div className="bg-gradient-to-r from-[#df1283]/20 to-[#FF7901]/20 rounded-lg p-4 border border-[#df1283]/50">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={notification.send_as_email}
              onChange={(e) => setNotification({...notification, send_as_email: e.target.checked})}
              className="w-5 h-5 mt-0.5 rounded"
            />
            <div>
              <span className="text-sm font-semibold text-white flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#df1283]" />
                Also send as email
              </span>
              <p className="text-xs text-[#FDF2F8]/60 mt-1">Reaches users who haven&apos;t logged in.</p>
            </div>
          </label>
        </div>

        {/* Targeting */}
        <TargetingSection notification={notification} setNotification={setNotification} targetingUsers={targetingUsers} />

        {/* Scheduling */}
        <div className="bg-[#4A0A66]/20 rounded-lg p-4 border border-[#4A0A66]/50">
          <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#FF7901]" />
            Scheduling & Expiration
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#FDF2F8]/60 mb-1">Schedule for (optional)</label>
              <input
                type="datetime-local"
                value={notification.scheduled_at}
                onChange={(e) => setNotification({...notification, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded-md px-3 py-2 text-white text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-[#FDF2F8]/60 mb-1">Expires on (optional)</label>
              <input
                type="datetime-local"
                value={notification.expires_at}
                onChange={(e) => setNotification({...notification, expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''})}
                className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded-md px-3 py-2 text-white text-sm"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <ActionButtonsSection
          notification={notification}
          addActionButton={addActionButton}
          updateActionButton={updateActionButton}
          removeActionButton={removeActionButton}
        />

        {/* Submit */}
        <div className="flex gap-3 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1 border-[#4A0A66] text-[#FDF2F8]/70">
            Cancel
          </Button>
          <Button onClick={handleCreate} className="flex-1 bg-gradient-to-r from-[#df1283] to-[#FF7901]">
            <Send className="w-4 h-4 mr-1" />
            {notification.scheduled_at ? 'Schedule' : 'Send Now'}
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const TargetingSection = ({ notification, setNotification, targetingUsers }) => (
  <div className="bg-[#4A0A66]/20 rounded-lg p-4 border border-[#4A0A66]/50">
    <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
      <Target className="w-4 h-4 text-[#0FFFE2]" />
      Targeting
    </h4>
    <div className="space-y-3">
      <div className="flex gap-2">
        {['all', 'filter', 'specific'].map((type) => (
          <button
            key={type}
            onClick={() => setNotification({...notification, target_type: type, target_user_ids: [], target_filter: ''})}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              notification.target_type === type ? 'bg-[#0FFFE2] text-black' : 'bg-[#4A0A66]/50 text-[#FDF2F8]/70 hover:bg-[#4A0A66]'
            }`}
          >
            {type === 'all' ? 'All Users' : type === 'filter' ? 'By Filter' : 'Select Users'}
          </button>
        ))}
      </div>

      {notification.target_type === 'filter' && (
        <select
          value={notification.target_filter}
          onChange={(e) => setNotification({...notification, target_filter: e.target.value})}
          className="w-full bg-[#4A0A66]/30 border border-[#4A0A66] rounded-md px-3 py-2 text-white text-sm"
        >
          <option value="">Select filter...</option>
          <option value="active_last_7_days">Active in last 7 days</option>
          <option value="inactive_7_days">Inactive for 7+ days</option>
          <option value="new_this_week">Joined this week</option>
          <option value="no_entries">No journal entries</option>
          <option value="has_streak">Has active streak</option>
        </select>
      )}

      {notification.target_type === 'specific' && (
        <div className="space-y-2">
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={notification.target_user_search || ''}
            onChange={(e) => setNotification({...notification, target_user_search: e.target.value})}
            className="w-full bg-[#1a0527] border border-[#4A0A66] rounded-lg px-3 py-2 text-white text-sm"
          />
          <div className="max-h-40 overflow-y-auto space-y-1 bg-[#1a0527] rounded-lg p-2">
            {targetingUsers.length === 0 ? (
              <p className="text-xs text-[#FDF2F8]/50 text-center py-2">Loading users...</p>
            ) : (
              targetingUsers
                .filter(user => {
                  if (!notification.target_user_search) return true;
                  const search = notification.target_user_search.toLowerCase();
                  return user.email?.toLowerCase().includes(search) || user.first_name?.toLowerCase().includes(search) || user.name?.toLowerCase().includes(search);
                })
                .map((user) => (
                  <label key={user.user_id} className="flex items-center gap-2 p-2 rounded hover:bg-[#4A0A66]/30 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notification.target_user_ids.includes(user.user_id)}
                      onChange={(e) => {
                        const ids = e.target.checked
                          ? [...notification.target_user_ids, user.user_id]
                          : notification.target_user_ids.filter(id => id !== user.user_id);
                        setNotification({...notification, target_user_ids: ids});
                      }}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm text-white">{user.first_name || user.name || user.email}</span>
                    <span className="text-xs text-[#FDF2F8]/40">{user.email}</span>
                  </label>
                ))
            )}
          </div>
          {notification.target_user_ids.length > 0 && (
            <p className="text-xs text-[#0FFFE2]">{notification.target_user_ids.length} user(s) selected</p>
          )}
        </div>
      )}
    </div>
  </div>
);

const ActionButtonsSection = ({ notification, addActionButton, updateActionButton, removeActionButton }) => (
  <div className="bg-[#4A0A66]/20 rounded-lg p-4 border border-[#4A0A66]/50">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-semibold text-white flex items-center gap-2">
        <MousePointer className="w-4 h-4 text-[#df1283]" />
        Action Buttons (optional)
      </h4>
      {notification.action_buttons.length < 2 && (
        <button onClick={addActionButton} className="text-xs text-[#0FFFE2] hover:underline">+ Add Button</button>
      )}
    </div>
    {notification.action_buttons.length === 0 ? (
      <p className="text-xs text-[#FDF2F8]/50">No action buttons. Users will see a &quot;Got it!&quot; dismiss button.</p>
    ) : (
      <div className="space-y-3">
        {notification.action_buttons.map((button, idx) => (
          <div key={`action-btn-${idx}`} className="flex items-center gap-2 bg-[#1a0527] rounded-lg p-3">
            <div className="flex-1 grid grid-cols-3 gap-2">
              <Input
                value={button.text}
                onChange={(e) => updateActionButton(idx, 'text', e.target.value)}
                placeholder="Button text"
                className="bg-[#4A0A66]/30 border-[#4A0A66] text-white text-sm"
              />
              <Input
                value={button.url}
                onChange={(e) => updateActionButton(idx, 'url', e.target.value)}
                placeholder="URL (e.g., /journal)"
                className="bg-[#4A0A66]/30 border-[#4A0A66] text-white text-sm"
              />
              <select
                value={button.style}
                onChange={(e) => updateActionButton(idx, 'style', e.target.value)}
                className="bg-[#4A0A66]/30 border border-[#4A0A66] rounded-md px-2 text-white text-sm"
              >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
              </select>
            </div>
            <button onClick={() => removeActionButton(idx)} className="text-red-400 hover:text-red-300 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default NotificationsTabContent;
